import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { TID } from "@/lib/testIds";

const LINKS = [
  { label: "What's New", href: "#whats-new", tid: "nav-link-whats-new" },
  { label: "About", href: "#about", tid: TID.nav.linkAbout },
  { label: "Philosophy", href: "#philosophy", tid: TID.nav.linkPhilosophy },
  { label: "Programs", href: "#programs", tid: TID.nav.linkPrograms },
  { label: "Testimonials", href: "#testimonials", tid: TID.nav.linkTestimonials },
  { label: "Contact", href: "#contact", tid: TID.nav.linkContact },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <motion.nav
      data-testid={TID.nav.root}
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-[#060b14]/85 backdrop-blur-md border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between gap-6">
        <a
          href="#top"
          data-testid={TID.nav.logo}
          className="flex items-center gap-3 group shrink-0"
        >
          <img
            src="https://customer-assets-39nsmqrw.emergentagent.net/job_59d46705-6dcb-49ae-8336-02ffaa8344ff/artifacts/ehbe4kg0_ChatGPT%20Image%20Jul%2024%2C%202026%2C%2008_44_18%20PM.png"
            alt="Coast 2 Coast Basketball Academy"
            className="h-10 w-10 md:h-11 md:w-11 object-cover border border-white/10"
          />
          <span className="font-display uppercase text-xl md:text-2xl tracking-tight leading-none">
            Coast<span className="text-[#2a5fdb]">2</span>Coast
          </span>
          <span className="hidden xl:inline font-mono text-[10px] tracking-[0.25em] text-muted uppercase pt-1 pl-2 border-l border-white/15 ml-1">
            Basketball Academy
          </span>
        </a>

        <div className="hidden md:flex items-center gap-6 lg:gap-10">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.tid}
              className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/80 hover:text-white link-underline"
            >
              {l.label}
            </a>
          ))}
          <a
            href="/book"
            data-testid={TID.nav.ctaBook}
            className="btn-sharp"
          >
            Book a session
            <span aria-hidden>→</span>
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="md:hidden h-11 w-11 flex items-center justify-center border border-white/15 text-white"
          aria-label="Menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden fixed inset-0 top-[72px] bg-[#060b14]/98 backdrop-blur-md border-t border-white/10 flex flex-col px-6 pt-8 pb-10 overflow-y-auto"
        >
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="py-4 border-b border-white/10 font-display uppercase text-3xl tracking-tight text-white"
            >
              {l.label}
            </a>
          ))}
          <a
            href="/book"
            data-testid="nav-mobile-cta-book"
            onClick={() => setOpen(false)}
            className="btn-sharp mt-8 justify-center"
          >
            Book a session
            <span aria-hidden>→</span>
          </a>
        </div>
      )}
    </motion.nav>
  );
};
